import { useState, useContext } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import IconButton from '@mui/material/IconButton'
import EditIcon from '@mui/icons-material/Edit'
import { QuilttLogo } from 'components/QuilttLogo'
import { testIds } from '../constants'
import { Box, Typography } from '@mui/material'
import { UpdateFieldModalForm } from './UpdateFieldModalForm'

// MUI Account Icons
import {
  AccountBalance,
  Cancel,
  Check,
  CheckBox,
  CheckBoxOutlineBlank,
  Close,
  CreditCard,
  Home,
  LocalHospital,
  MedicalServices,
  People,
  RequestQuote,
  Savings,
  TaskAlt,
  VolunteerActivism,
} from '@mui/icons-material'

// MUI Status Icons
import LinkIcon from '@mui/icons-material/Link'
import HistoryEduIcon from '@mui/icons-material/HistoryEdu'
import styled from 'styled-components'

import { format } from 'date-fns'

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

// category prop is `subType` as it exists now in graphql - tbd
// in the serverless.js environment
export const Assets = ({ assets, category, handleOnClick }) => {
  const { intakeState, updateUser, updateIntakeStateAtKey, userTasks } =
    useContext(IntakeContext)
  const [openFieldModal, setOpenFieldModal] = useState(false)
  const [updateFieldModalTitle, setUpdateFieldModalTitle] = useState(
    'Update Your TSP Balance'
  )
  const [updateFieldModalUserField, setUpdateFieldModalUserField] =
    useState('tspBalance')
  const handleFieldModalOpenTSP = () => {
    setUpdateFieldModalTitle('Thrift Savings Plan (Traditional)')
    setUpdateFieldModalUserField('tspBalance')
    setOpenFieldModal(true)
  }
  const handleFieldModalOpenTSPRoth = () => {
    setUpdateFieldModalTitle('Thrift Savings Plan (Roth)')
    setUpdateFieldModalUserField('tspRothBalance')
    setOpenFieldModal(true)
  }
  const handleFieldModalClose = () => {
    setOpenFieldModal(false)
    updateUser()
  }

  const AccountIconSelector = ({ subType, ...props }) => {
    const iconMap = {
      checkingsavings: Savings,
      property: Home,
      creditcard: CreditCard,
      investment: AccountBalance,
      mortgage: Home,
      charity: VolunteerActivism,
      alotchecking: Savings,
      alotother: Savings,
      alotsavings: Savings,
      fdvv: LocalHospital,
      fdvd: LocalHospital,
      ltc: LocalHospital,
      visiondental: LocalHospital,
      extratax: RequestQuote,
      uniondues: People,
      fsa: MedicalServices,
      hsa: MedicalServices,
      vbp: MedicalServices,
    }
    const IconComponent = iconMap[subType] || AccountBalance
    return <IconComponent {...props} />
  }

  const StyledInstitutionLogo = styled.img`
    max-width: 30px;
    max-height: 30px;
    min-height: 20px;
    width: auto;
    border-radius: 50%;
    margin-right: 10px;
  `
  return (
    <>
      <UpdateFieldModalForm
        handleClose={handleFieldModalClose}
        open={openFieldModal}
        handleSubmit={handleFieldModalClose}
        title={updateFieldModalTitle}
        userField={updateFieldModalUserField}
      />
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          border: '1px solid #ddd',
          marginBottom: '16px',
          '&:empty': {
            border: 'none',
            marginBottom: '0',
          },
        }}
        data-testid={`${testIds.AssetContainer}${category}`}
      >
        {category === 'AllAssets' ? (
          <>
            <Box
              onClick={() => handleFieldModalOpenTSP()}
              sx={{
                cursor: 'pointer',
                borderBottom: '1px solid #ddd',
                '&:first-of-type': {
                  borderRadius: '8px 8px 0 0',
                },
                '&:last-of-type': {
                  borderBottom: 'none',
                  borderRadius: '0 0 8px 8px',
                },
                '&:hover': {
                  backgroundColor: '#fafafb',
                },
              }}
            >
              <Box
                pl={1.5}
                pr={2}
                py={1.5}
                display="flex"
                justifyContent="space-between"
                color="#262627"
              >
                <Box display="flex" color="#262627">
                  <IconButton
                    aria-label="edit"
                    size="small"
                    sx={{
                      marginTop: '-2px',
                      marginRight: '10px',
                      padding: '6px',
                    }}
                  >
                    <AccountBalance fontSize="inherit" />
                  </IconButton>
                  <Typography textAlign="left" color="#262627" variant="body2">
                    Thrift Savings Plan (Traditional)
                  </Typography>
                </Box>
                <Box display="flex" color="#262627">
                  <Typography textAlign="right" color="#262627" variant="body2">
                    {dollarFormat.format(intakeState?.tspBalance || 0)}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              onClick={() => handleFieldModalOpenTSPRoth()}
              sx={{
                cursor: 'pointer',
                borderBottom: '1px solid #ddd',
                '&:first-of-type': {
                  borderRadius: '8px 8px 0 0',
                },
                '&:last-of-type': {
                  borderBottom: 'none',
                  borderRadius: '0 0 8px 8px',
                },
                '&:hover': {
                  backgroundColor: '#fafafb',
                },
              }}
            >
              <Box
                pl={1.5}
                pr={2}
                py={1.5}
                display="flex"
                justifyContent="space-between"
                color="#262627"
              >
                <Box display="flex" color="#262627">
                  <IconButton
                    aria-label="edit"
                    size="small"
                    sx={{
                      marginTop: '-2px',
                      marginRight: '10px',
                      padding: '6px',
                    }}
                  >
                    <AccountBalance fontSize="inherit" />
                  </IconButton>
                  <Typography textAlign="left" color="#262627" variant="body2">
                    Thrift Savings Plan (Roth)
                  </Typography>
                </Box>
                <Box display="flex" color="#262627">
                  <Typography textAlign="right" color="#262627" variant="body2">
                    {dollarFormat.format(intakeState?.tspRothBalance || 0)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <></>
        )}
        {(assets || []).map((asset, i) => {
          const {
            name,
            amount,
            externalAsset,
            externalName,
            typeSubTypeUuid,
            updatedAt,
            externalInstitutionLogo,
            type,
            keepInRetirement,
            subType,
          } = asset
          const rowTestId = `${testIds.AssetsRow}${
            category ? `${category}-` : ''
          }${i}`
          return (
            <Box
              onClick={() => handleOnClick(asset)}
              sx={{
                cursor: 'pointer',
                borderBottom: '1px solid #ddd',
                '&:first-of-type': {
                  borderRadius: '8px 8px 0 0',
                },
                '&:last-of-type': {
                  borderBottom: 'none',
                  borderRadius: '0 0 8px 8px',
                },
                '&:hover': {
                  backgroundColor: '#fafafb',
                },
              }}
              key={`${typeSubTypeUuid}-${name}`}
              data-testid={rowTestId}
            >
              <Box
                pl={1.5}
                pr={2}
                py={1.5}
                display="flex"
                justifyContent="space-between"
                color="#262627"
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  color="#262627"
                  width="82%"
                  className="assetTitleDateBox"
                >
                  <Box
                    display="flex"
                    color="#262627"
                    width="54%"
                    alignItems="center"
                  >
                    {externalInstitutionLogo ? (
                      <StyledInstitutionLogo
                        src={`${externalInstitutionLogo}`}
                      />
                    ) : externalInstitutionLogo === '' ? (
                      <IconButton
                        aria-label="edit"
                        size="small"
                        sx={{
                          marginTop: '-2px',
                          marginRight: '10px',
                          padding: '6px',
                          backgroundColor: '#293852',
                          color: '#f9f9f9',
                        }}
                      >
                        <AccountIconSelector subType={subType} />
                      </IconButton>
                    ) : (
                      <IconButton
                        aria-label="edit"
                        size="small"
                        sx={{
                          marginTop: '-2px',
                          marginRight: '10px',
                          padding: '6px',
                        }}
                      >
                        <AccountIconSelector subType={subType} />
                      </IconButton>
                    )}

                    <Typography
                      textAlign="left"
                      color="#262627"
                      variant="body2"
                    >
                      {externalName ? externalName : name}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    color="#A3A7AA"
                    width="50%"
                    alignItems="center"
                  >
                    <IconButton
                      aria-label="edit"
                      size="small"
                      sx={{ marginTop: '-2px', marginRight: '5px' }}
                    >
                      {externalAsset ? (
                        <LinkIcon fontSize="inherit" />
                      ) : (
                        <HistoryEduIcon fontSize="inherit" />
                      )}
                    </IconButton>
                    <Typography
                      textAlign="left"
                      color="#A3A7AA"
                      variant="body2"
                    >
                      Updated {format(new Date(updatedAt), 'MM/dd/yyyy')}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" color="#262627">
                  <Box display="flex" alignItems="center">
                    {type === 'deduction' &&
                    subType !== 'charity' &&
                    subType !== 'fsa' &&
                    subType !== 'hsa' &&
                    subType !== 'alotsavings' &&
                    subType !== 'alotchecking' &&
                    subType !== 'alotother' ? (
                      <Typography
                        textAlign="right"
                        color={keepInRetirement ? 'green' : 'red'}
                        variant="body2"
                        sx={{
                          marginRight: '10px',
                          '&>svg': {
                            marginRight: '5px',
                            marginBottom: '-2px',
                          },
                        }}
                      >
                        {keepInRetirement ? (
                          <>
                            <Check />
                            Keeping in Retirement
                          </>
                        ) : (
                          <>NOT Keeping in Retirement</>
                        )}
                      </Typography>
                    ) : (
                      <></>
                    )}
                    <Typography
                      textAlign="right"
                      color="#262627"
                      variant="body2"
                    >
                      {dollarFormat.format(amount)}
                    </Typography>
                  </Box>
                  {/* <IconButton
                  aria-label="edit"
                  size="small"
                  sx={{ marginTop: '-2px', marginLeft: '5px' }}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton> */}
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>
    </>
  )
}
